// The variant monitoring worklist's requests (TGCLOUD-1899; backend/routes/variant-reclassification.js,
// the contract in its header). Reads go through React Query so the list, the counts, the bell's
// line and a variant's banner all refresh after an acknowledgement; the one write is a POST whose
// answer says how many events it covered.
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'src/axios';
import { APICONSTANTS } from 'src/components/Constants';
import { NOTIFICATION_FEED_KEY } from 'src/hooks/useNotificationFeed';
import { translate } from 'src/hooks/useTranslate';

export const variantMonitoringKeys = Object.freeze({
  all: ['variantMonitoring'],
  list: (params) => ['variantMonitoring', 'list', params],
  summary: ['variantMonitoring', 'summary'],
  record: (userId, familyMemberId) => [
    'variantMonitoring',
    'record',
    String(userId || ''),
    String(familyMemberId || '')
  ]
});

// A refusal is an answer, not a blip: retry only what may be one (no answer at all, or a 5xx).
const retryTransient = (count, err) => {
  const status = err?.response?.status;
  return (!status || status >= 500) && count < 2;
};

const READ_OPTIONS = { staleTime: 15_000, retry: retryTransient };

/**
 * The events of one view (GET /variant-reclassification): `view` is 'open' (unacknowledged, in
 * the default-view directions) or 'all'; `source` narrows to 'clinvar' or 'gencc'. Rows carry the
 * person and the record's current classifications.
 */
export function useReclassificationEvents(
  { view = 'open', source = null } = {},
  { enabled = true } = {}
) {
  const params = { view, limit: 200, ...(source ? { source } : {}) };
  return useQuery({
    queryKey: variantMonitoringKeys.list(params),
    queryFn: async () =>
      (await axios.get(APICONSTANTS.variantReclassification, { params })).data,
    enabled,
    placeholderData: (previous) => previous,
    ...READ_OPTIONS
  });
}

/** The open counts by source and each sweep's last run (GET /variant-reclassification/summary). */
export function useReclassificationSummary({ enabled = true } = {}) {
  return useQuery({
    queryKey: variantMonitoringKeys.summary,
    queryFn: async () =>
      (await axios.get(APICONSTANTS.variantReclassificationSummary)).data,
    enabled,
    ...READ_OPTIONS
  });
}

/** The open events about one person's record, for the banner on a variant. */
export function useRecordReclassifications(
  { userId, familyMemberId, proband = false } = {},
  { enabled = true } = {}
) {
  const url = APICONSTANTS.variantReclassificationRecord
    .replace(':user_id', encodeURIComponent(userId || ''))
    .replace(
      '/:familyMemberId',
      familyMemberId ? `/${encodeURIComponent(familyMemberId)}` : ''
    );
  return useQuery({
    queryKey: variantMonitoringKeys.record(userId, familyMemberId),
    queryFn: async () =>
      (await axios.get(url, { params: proband ? { proband: true } : {} })).data
        ?.events || [],
    enabled: enabled && Boolean(userId),
    ...READ_OPTIONS
  });
}

/**
 * Acknowledge events for the group (POST /variant-reclassification/acknowledge). Everything that
 * shows them reads again: the worklist, the summary, a variant's banner and the bell's line.
 */
export function useAcknowledgeEvents() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (eventIds) =>
      (
        await axios.post(APICONSTANTS.variantReclassificationAcknowledge, {
          eventIds
        })
      ).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: variantMonitoringKeys.all });
      queryClient.invalidateQueries({ queryKey: [NOTIFICATION_FEED_KEY] });
    }
  });
}

const BY_CODE = {
  VARIANT_MONITORING_OFF:
    'Variant reclassification monitoring is switched off for this group.'
};

/** The sentence for a refused read or write. PURE. */
export function describeVariantMonitoringError(err, fallback) {
  const data = err?.response?.data || {};
  if (data.code && BY_CODE[data.code]) return translate(BY_CODE[data.code]);
  const status = err?.response?.status;
  if (status === 403 && data.message === 'Forbidden') {
    return translate(
      'Your security group does not include Variant Monitoring.'
    );
  }
  if (status === 403 && data.message === 'Read-only access') {
    return translate(
      'Your access is read-only, so you can see these changes but not acknowledge them.'
    );
  }
  return fallback;
}
